import { z } from "zod";
import { AiManagedGroupPolicySchema } from "./ai-managed-group";
import { BusinessGoalV1Schema } from "./ai-policy";

const BusinessGoalAdjustmentSchema = z
  .object({
    businessGoal: BusinessGoalV1Schema,
    factor: z.number().finite().positive(),
    direction: z.enum(["decrease", "increase", "unchanged"]),
  })
  .strict()
  .refine(
    ({ direction, factor }) =>
      (factor < 1 && direction === "decrease") ||
      (factor > 1 && direction === "increase") ||
      (factor === 1 && direction === "unchanged"),
    { message: "direction must match the business goal adjustment factor" },
  );

const diagnosticsCampaignIdsSchema = z
  .array(z.string().uuid())
  .min(1)
  .max(100)
  .superRefine((ids, context) => {
    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "campaignIds must not contain duplicates",
      });
    }
  });

export const AiManagedGroupDiagnosticsInputSchema = z
  .object({
    schemaVersion: z.literal(1),
    campaignIds: diagnosticsCampaignIdsSchema,
    policy: AiManagedGroupPolicySchema,
    lifecycleStatus: z.enum(["active", "paused"]),
  })
  .strict();
export type AiManagedGroupDiagnosticsInput = z.infer<typeof AiManagedGroupDiagnosticsInputSchema>;

export const AiManagedGroupDiagnosticReasonCodeSchema = z.enum([
  "campaign_not_found_or_other_shop",
  "unsupported_campaign_type",
  "campaign_archived",
  "campaign_already_assigned",
  "empty_group",
  "workspace_ai_off",
  "report_coverage_low",
  "no_activity",
  "clicks_low",
  "no_conversions",
  "budget_near_exhaustion",
  "budget_limited",
  "acos_spread_high",
  "mixed_ai_state",
  "existing_ai_policy_will_sleep",
]);
export type AiManagedGroupDiagnosticReasonCode = z.infer<
  typeof AiManagedGroupDiagnosticReasonCodeSchema
>;

export const AiManagedGroupDiagnosticReasonSchema = z
  .object({
    code: AiManagedGroupDiagnosticReasonCodeSchema,
    severity: z.enum(["blocked", "attention"]),
    campaignIds: z.array(z.string().uuid()),
  })
  .strict();
export type AiManagedGroupDiagnosticReason = z.infer<typeof AiManagedGroupDiagnosticReasonSchema>;

export const AiManagedGroupDiagnosticsSchema = z
  .object({
    schemaVersion: z.literal(1),
    classification: z.enum(["blocked", "attention", "suitable"]),
    observedAt: z.string().datetime(),
    window: z
      .object({
        startDate: z.string().date(),
        endDate: z.string().date(),
        dataThrough: z.string().date().nullable(),
        totalDays: z.literal(14),
        coveredDays: z.number().int().min(0).max(14),
      })
      .strict(),
    metrics: z
      .object({
        impressions: z.number().int().nonnegative(),
        clicks: z.number().int().nonnegative(),
        spend: z.number().nonnegative(),
        sales: z.number().nonnegative(),
        orders: z.number().int().nonnegative(),
        actualAcos: z.number().nonnegative().nullable(),
      })
      .strict(),
    summary: z
      .object({
        campaignCount: z.number().int().nonnegative(),
        aiOnCount: z.number().int().nonnegative(),
        aiOffCount: z.number().int().nonnegative(),
        budgetNearExhaustionCount: z.number().int().nonnegative(),
        budgetLimitedCount: z.number().int().nonnegative(),
        budgetUnknownCount: z.number().int().nonnegative(),
        acosMin: z.number().nonnegative().nullable(),
        acosMax: z.number().nonnegative().nullable(),
      })
      .strict(),
    reasons: z.array(AiManagedGroupDiagnosticReasonSchema),
    recommendation: z
      .object({
        targetAcos: z.number().min(0.01).max(2),
        baselineAcos: z.number().nonnegative(),
        businessGoalAdjustment: BusinessGoalAdjustmentSchema,
        rangeMin: z.number().min(0.01).max(2),
        rangeMax: z.number().min(0.01).max(2),
        basedOnCoveredDays: z.number().int().min(10).max(14),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type AiManagedGroupDiagnostics = z.infer<typeof AiManagedGroupDiagnosticsSchema>;

export const AiManagedGroupDiagnosticsResponseSchema = z
  .object({ success: z.literal(true), data: AiManagedGroupDiagnosticsSchema })
  .strict();
